import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {JsonSchema} from '../schema-form.types';
import {isValueField} from '../widget-registry';
import {SfSelectComponent} from './sf-select.component';
import {SfInputComponent} from './sf-input.component';
import {SfChipsComponent} from './sf-chips.component';
import {
  createDefaultValueField,
  DATA_TYPES,
  DataType,
  IDSValueField,
} from '../../../checks/bimCheck/components/ids/models/ids.model';

/** Platzhalter des Werts je `x-ids-value-kind` – wie die Beispiele im IDS-Editor. */
const PLATZHALTER: Record<string, string> = {
  'ifc-class': 'z.B. IfcWall',
  'predefined-type': 'z.B. SOLIDWALL',
  'pset': 'z.B. Pset_WallCommon',
  'property': 'z.B. FireRating',
  'attribute': 'z.B. Name',
};

const LEER: IDSValueField = Object.freeze(createDefaultValueField()) as IDSValueField;
const ART_SCHEMA: JsonSchema = {enum: [...DATA_TYPES]};
const MIN_SCHEMA: JsonSchema = {'x-placeholder': 'Minimum'};
const MAX_SCHEMA: JsonSchema = {'x-placeholder': 'Maximum'};
const MUSTER_SCHEMA: JsonSchema = {'x-placeholder': 'Regulärer Ausdruck, z.B. F[0-9]+'};
const LAENGE_SCHEMA: JsonSchema = {'x-placeholder': 'Anzahl Zeichen'};

/**
 * Ein IDS-Wertfeld (`x-widget: ids-value`) – dieselben sechs Wert-Arten wie im IDS-Editor
 * (Wert, Aufzählung, Grenzen, Muster, Länge, Länge Grenzen) und dieselbe Datenform `IDSValueField`.
 *
 * Für die IDS-Karten der IFC-Bausteine (Selektion/Prüfung), die wie der IDS-Check auswerten. Bewusst
 * eine eigene, schmale Komponente statt der Wertzeile des IDS-Editors: die ist dort mit den
 * Änderungsmarkierungen der chatBIC-Vorschläge und der Fehleranzeige des Editors verwoben, und das
 * Panel des Workflow-Editors ist nur ~400 px breit (Art über dem Wert statt daneben).
 *
 * Gebaut aus den vorhandenen Widgets (sf-select, sf-input, sf-chips) – kein eigenes Styling.
 */
@Component({
  selector: 'sf-ids-value',
  standalone: true,
  imports: [SfSelectComponent, SfInputComponent, SfChipsComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-2 w-full">
      <sf-select [schema]="artSchema" [value]="feld.dataType" [disabled]="disabled"
                 (valueChange)="setArt($event)"></sf-select>
      @switch (feld.dataType) {
        @case ('Aufzählung') {
          <sf-chips [schema]="wertSchema" [value]="feld.enumValues"
                    (valueChange)="set({enumValues: $event})"></sf-chips>
        }
        @case ('Grenzen') {
          <div class="flex gap-2 w-full">
            <sf-input class="flex-1 min-w-0" [schema]="minSchema" [value]="feld.rangeMin" [disabled]="disabled"
                      (valueChange)="set({rangeMin: $event})"></sf-input>
            <sf-input class="flex-1 min-w-0" [schema]="maxSchema" [value]="feld.rangeMax" [disabled]="disabled"
                      (valueChange)="set({rangeMax: $event})"></sf-input>
          </div>
        }
        @case ('Muster') {
          <sf-input [schema]="musterSchema" [value]="feld.pattern" [disabled]="disabled"
                    (valueChange)="set({pattern: $event})"></sf-input>
        }
        @case ('Länge') {
          <sf-input [schema]="laengeSchema" [value]="feld.lengthValue" [disabled]="disabled"
                    (valueChange)="set({lengthValue: $event})"></sf-input>
        }
        @case ('Länge Grenzen') {
          <div class="flex gap-2 w-full">
            <sf-input class="flex-1 min-w-0" [schema]="minSchema" [value]="feld.lengthMin" [disabled]="disabled"
                      (valueChange)="set({lengthMin: $event})"></sf-input>
            <sf-input class="flex-1 min-w-0" [schema]="maxSchema" [value]="feld.lengthMax" [disabled]="disabled"
                      (valueChange)="set({lengthMax: $event})"></sf-input>
          </div>
        }
        @default {
          <sf-input [schema]="wertSchema" [value]="feld.simpleValue" [disabled]="disabled"
                    (valueChange)="set({simpleValue: $event})"></sf-input>
        }
      }
    </div>
  `,
})
export class SfIdsValueComponent {
  @Input() set schema(schema: JsonSchema | null) {
    // Einmal je Schema statt in einem Getter: Inputs dürfen kein in jedem Lauf neues Objekt bekommen.
    const kind = schema?.['x-ids-value-kind'] ?? '';
    this.wertSchema = {'x-placeholder': schema?.['x-placeholder'] ?? PLATZHALTER[kind] ?? 'Wert eingeben…'};
  }
  @Input() value: any;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<IDSValueField | null>();

  protected wertSchema: JsonSchema = {'x-placeholder': 'Wert eingeben…'};
  protected readonly artSchema = ART_SCHEMA;
  protected readonly minSchema = MIN_SCHEMA;
  protected readonly maxSchema = MAX_SCHEMA;
  protected readonly musterSchema = MUSTER_SCHEMA;
  protected readonly laengeSchema = LAENGE_SCHEMA;

  /** Der gespeicherte Wert – ein neues Kartenfeld steht als '' oder null in der Karte (buildDefault, x-resets). */
  get feld(): IDSValueField {
    return isValueField(this.value) ? this.value : LEER;
  }

  /** Wechsel der Wert-Art beginnt leer – wie im IDS-Editor gehören die Felder der alten Art nicht zur neuen. */
  setArt(dataType: DataType): void {
    if (dataType === this.feld.dataType) return;
    this.valueChange.emit({...createDefaultValueField(), dataType});
  }

  set(teil: Partial<IDSValueField>): void {
    this.valueChange.emit({...this.feld, ...teil});
  }
}
